import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Counter from 'LuxKit/Counter'
import TextButton from 'LuxKit/Button/TextButton'
import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import type { TripDay, TripStop } from 'tripPlanner/api/types'
import { doesStayExceedTripDates } from 'tripPlanner/utils/chatItinerary'
import { getDayLabel, getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import ExtendTripModal from './ExtendTripModal'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'

const DayFieldGroup = styled(Group)`
  width: 100%;

  ${mediaQueryUp.tablet} {
    flex: 0 1 ${rem(220)};
    width: ${rem(220)};
  }
`

const NightsFieldGroup = styled(Group)`
  flex: 0 0 auto;
  width: max-content;
  max-width: 100%;
`

const DaySelectButton = styled(TextButton)`
  justify-content: space-between;
`

const StayCounter = styled(Counter)`
  width: max-content;
  min-width: ${rem(140)};
  max-width: 100%;
  justify-content: space-between;
`

const FooterActions = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: ${rem(8)};
  width: 100%;

  ${mediaQueryUp.tablet} {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
  }
`

const ConfirmationActions = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
  }
`

interface DayOption {
  dayNumber: number
  date?: string
  cityName: string
}

function buildDayOptionsForStop(stop: TripStop): Array<DayOption> {
  const options = stop.days.map((day: TripDay) => ({
    dayNumber: day.dayNumber,
    date: day.date,
    cityName: stop.placeName,
  }))

  if (options.length === 0) {
    return [{ dayNumber: 1, cityName: stop.placeName }]
  }

  return options
}

interface Props {
  isOpen: boolean
  hotelName: string
  relevantStop?: TripStop
  defaultDayNumber?: number
  defaultNights?: number
  /** Offer minimum length of stay; nights counter cannot go below this. */
  minNights?: number
  mode?: 'add' | 'update'
  isMutationPending?: boolean
  onClose: () => void
  onSubmit: (message: string) => void
  onRemove?: () => void
}

function AddHotelToTripModal({
  isOpen,
  hotelName,
  relevantStop,
  defaultDayNumber = 1,
  defaultNights = 1,
  minNights = 1,
  mode = 'add',
  isMutationPending = false,
  onClose,
  onSubmit,
  onRemove,
}: Props) {
  const intl = useIntl()
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const isUnmatchedStop = !relevantStop
  const targetStop = relevantStop
  const nightsFloor = minNights > 0 ? minNights : 1
  const initialNights = Math.max(defaultNights, nightsFloor)
  const dayOptions = useMemo(
    () => !isUnmatchedStop && targetStop ? buildDayOptionsForStop(targetStop) : [],
    [isUnmatchedStop, targetStop],
  )
  const [selectedDay, setSelectedDay] = useState<DayOption | undefined>(() =>
    dayOptions.find(day => day.dayNumber === defaultDayNumber) ?? dayOptions[0],
  )
  const [nights, setNights] = useState(initialNights)
  const [isDayDropdownOpen, setIsDayDropdownOpen] = useState(false)
  const [extendState, setExtendState] = useState<{ isOpen: boolean; extraDays: number }>({
    isOpen: false,
    extraDays: 0,
  })
  const dayButtonRef = useRef<HTMLButtonElement>(null)
  const wasOpenRef = useRef(isOpen)

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (!justOpened) return

    setSelectedDay(dayOptions.find(day => day.dayNumber === defaultDayNumber) ?? dayOptions[0])
    setNights(initialNights)
    setIsDayDropdownOpen(false)
    setExtendState({ isOpen: false, extraDays: 0 })
  }, [dayOptions, defaultDayNumber, initialNights, isOpen])

  useEffect(() => {
    if (!isOpen) return
    setNights(previousNights => Math.max(previousNights, nightsFloor))
  }, [isOpen, nightsFloor])

  const handleNightsChange = useCallback((value: number) => {
    setNights(Math.max(value, nightsFloor))
  }, [nightsFloor])

  const handleDayDropdownOpen = useCallback(() => {
    setIsDayDropdownOpen(true)
  }, [])

  const handleDayDropdownClose = useCallback(() => {
    setIsDayDropdownOpen(false)
  }, [])

  const handleDaySelect = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const dayNumber = Number(event.currentTarget.dataset.dayNumber)
    if (isNaN(dayNumber)) return

    const option = dayOptions.find(day => day.dayNumber === dayNumber)
    if (option) setSelectedDay(option)
    setIsDayDropdownOpen(false)
  }, [dayOptions])

  const buildMessage = useCallback(() => {
    if (isUnmatchedStop) {
      const displayMessage = mode === 'update'
        ? intl.formatMessage(
            {
              id: 'aw3Yq8Qz7neB92RiK5lO',
              defaultMessage: 'Update "{hotelName}" stay to {nights, plural, one {# night} other {# nights}}',
              description: 'User chat message shown when updating the length of a hotel stay already in the trip',
            },
            { hotelName, nights },
          )
        : intl.formatMessage(
            {
              id: '9a483gOas6Nysi1NmYXI',
              defaultMessage: 'Add "{hotelName}" for {nights, plural, one {# night} other {# nights}}',
              description: 'User chat message shown when adding a hotel for a stay length without a matched trip stop',
            },
            { hotelName, nights },
          )
      const contextMessage = mode === 'update'
        ? `Update "${hotelName}" stay to ${nights} nights`
        : `Add "${hotelName}" for ${nights} nights`
      return appendContextTag(displayMessage, contextMessage, TRIP_PLANNER_ACTION_CONTEXT_LABEL)
    }

    if (!selectedDay) return undefined
    const endDay = selectedDay.dayNumber + nights
    const fromDisplayLabel = getLocalisedDayLabel(intl, selectedDay.dayNumber, selectedDay.date)
    const fromContextLabel = getDayLabel(selectedDay.dayNumber, selectedDay.date)
    const toDate = selectedDay.date ? moment(selectedDay.date).add(nights, 'days').format('YYYY-MM-DD') : undefined
    const toDisplayLabel = getLocalisedDayLabel(intl, endDay, toDate)
    const toContextLabel = getDayLabel(endDay, toDate)
    const displayMessage = intl.formatMessage(
      {
        id: '05Xk8n84P0XUpCggECPh',
        defaultMessage: 'Assign "{hotelName}" to stay from {fromLabel} to {toLabel} in {cityName}',
        description: 'User chat message shown when assigning a hotel stay to a date range in a specific city',
      },
      {
        hotelName,
        fromLabel: fromDisplayLabel,
        toLabel: toDisplayLabel,
        cityName: formatPlacePhrase({ location: { name: selectedDay.cityName }, language: intl.locale, intent: 'location' }),
      },
    )

    return appendContextTag(
      displayMessage,
      `Assign "${hotelName}" to stay from ${fromContextLabel} to ${toContextLabel} in ${selectedDay.cityName}`,
      TRIP_PLANNER_ACTION_CONTEXT_LABEL,
    )
  }, [hotelName, intl, isUnmatchedStop, mode, nights, selectedDay])

  const handleSubmit = useCallback(() => {
    if (!isUnmatchedStop && !selectedDay) return

    if (!isUnmatchedStop && targetStop && selectedDay) {
      const { exceeds, extraDays } = doesStayExceedTripDates(selectedDay.dayNumber, nights, targetStop)
      if (exceeds) {
        setExtendState({ isOpen: true, extraDays })
        return
      }
    }

    const message = buildMessage()
    if (message) onSubmit(message)
  }, [buildMessage, isUnmatchedStop, nights, onSubmit, selectedDay, targetStop])

  const handleExtendConfirm = useCallback(() => {
    setExtendState({ isOpen: false, extraDays: 0 })
    const message = buildMessage()
    if (message) onSubmit(message)
  }, [buildMessage, onSubmit])

  const handleExtendClose = useCallback(() => {
    setExtendState({ isOpen: false, extraDays: 0 })
  }, [])

  const handleClose = useCallback(() => {
    setSelectedDay(dayOptions.find(day => day.dayNumber === defaultDayNumber) ?? dayOptions[0])
    setNights(initialNights)
    setIsDayDropdownOpen(false)
    setExtendState({ isOpen: false, extraDays: 0 })
    onClose()
  }, [dayOptions, defaultDayNumber, initialNights, onClose])

  const nightsFormatter = useCallback((value: number) => {
    return intl.formatMessage(
      {
        id: '0Y7CYLTTLQVHQr1XuL12',
        defaultMessage: '{count, plural, one {# night} other {# nights}}',
        description: 'Night count for hotel trip assignment duration selector',
      },
      { count: value },
    )
  }, [intl])

  const isUpdate = mode === 'update'
  const actionsDisabled = isStreaming || isStreamOpen || isMutationPending || (!isUnmatchedStop && !selectedDay)
  const primaryActionText = isUpdate
    ? intl.formatMessage({
        id: 'uCjTDWMBDm9S499NCV7s',
        defaultMessage: 'Update',
        description: 'Primary action button to update hotel assignment in trip',
      })
    : intl.formatMessage(tripPlannerGlossaryMessages.addToTrip)

  return (
    <>
      <ModalBase isOpen={isOpen && !extendState.isOpen} onClose={handleClose} size="S">
        <ModalHeader
          title={intl.formatMessage(tripPlannerGlossaryMessages.addToTrip)}
          subtitle={hotelName}
          onCloseButtonClick={handleClose}
        />
        <ModalBody>
          <ModalContent>
            <Group direction="vertical" gap={8} horizontalAlign="start">
              <Group direction="vertical" tabletDirection="horizontal" gap={24} verticalAlign="start">
                <NightsFieldGroup direction="vertical" gap={8} horizontalAlign="start">
                  <BodyText variant="medium" weight="bold">
                    {intl.formatMessage({
                      id: 'Ryy4iNU6jcD3-4W9eFz1',
                      defaultMessage: 'Length of stay',
                      description: 'Label for the number of nights selection field when adding a hotel to trip',
                    })}
                  </BodyText>
                  <StayCounter
                    value={nights}
                    min={nightsFloor}
                    max={30}
                    onChange={handleNightsChange}
                    size="large"
                    valueFormatter={nightsFormatter}
                  />
                </NightsFieldGroup>
                {!isUnmatchedStop && (
                  <DayFieldGroup direction="vertical" gap={8}>
                    <BodyText variant="medium" weight="bold">
                      {intl.formatMessage({
                        id: 'dBVesFs_nfMyF8ADPi0T',
                        defaultMessage: 'Check-in',
                        description: 'Label for the check-in day selection field when adding a hotel to trip',
                      })}
                    </BodyText>
                    <DaySelectButton
                      ref={dayButtonRef}
                      kind="secondary"
                      size="large"
                      endIcon={<LineAngleDownIcon />}
                      onClick={handleDayDropdownOpen}
                      fit="full-width"
                    >
                      {selectedDay ? getLocalisedDayLabel(intl, selectedDay.dayNumber, selectedDay.date) : intl.formatMessage({
                        id: 'f6OvnZs4BqTTYR5XNvmx',
                        defaultMessage: 'Select day',
                        description: 'Placeholder text when no check-in day is selected for hotel trip assignment',
                      })}
                    </DaySelectButton>
                    <ResponsiveDropdownList
                      open={isDayDropdownOpen}
                      onClose={handleDayDropdownClose}
                      triggerRef={dayButtonRef}
                      anchorRef={dayButtonRef}
                      size="S"
                    >
                      {dayOptions.map(option => (
                        <ClickableListItem
                          key={option.dayNumber}
                          size="medium"
                          title={getLocalisedDayLabel(intl, option.dayNumber, option.date)}
                          subtitle={option.cityName}
                          data-day-number={option.dayNumber}
                          onClick={handleDaySelect}
                          selected={option.dayNumber === selectedDay?.dayNumber}
                        />
                      ))}
                    </ResponsiveDropdownList>
                  </DayFieldGroup>
                )}
              </Group>
              {nightsFloor > 1 && (
                <BodyText variant="medium" colour="neutral-two">
                  {intl.formatMessage(
                    {
                      id: '_mMZ6kBcn_63lNZdbzTp',
                      defaultMessage: 'Minimum stay for this offer is {minStay, plural, one {# night} other {# nights}}',
                      description: 'Subtitle showing minimum stay duration for the offer',
                    },
                    { minStay: nightsFloor },
                  )}
                </BodyText>
              )}
            </Group>
          </ModalContent>
        </ModalBody>
        <ModalFooter>
          <FooterActions>
            <div>
              {isUpdate && onRemove && (
                <TextButton kind="tertiary" size="large" fit="mobile-full-width" onClick={onRemove} disabled={actionsDisabled}>
                  {intl.formatMessage({
                    id: 'VfcWIJpjyFrGmr9e7R3R',
                    defaultMessage: 'Remove from trip',
                    description: 'Button label that removes an existing hotel from the trip planner itinerary',
                  })}
                </TextButton>
              )}
            </div>
            <ConfirmationActions>
              <TextButton kind="tertiary" size="large" fit="mobile-full-width" onClick={handleClose} disabled={isMutationPending}>
                {intl.formatMessage({
                  id: 't0-oeWZUrkVajvX2QR3v',
                  defaultMessage: 'Cancel',
                  description: 'Secondary action button to cancel adding hotel to trip',
                })}
              </TextButton>
              <TextButton kind="primary" size="large" fit="mobile-full-width" onClick={handleSubmit} disabled={actionsDisabled}>
                {primaryActionText}
              </TextButton>
            </ConfirmationActions>
          </FooterActions>
        </ModalFooter>
      </ModalBase>

      <ExtendTripModal
        isOpen={extendState.isOpen}
        hotelName={hotelName}
        extraDays={extendState.extraDays}
        onClose={handleExtendClose}
        onConfirm={handleExtendConfirm}
      />
    </>
  )
}

export default AddHotelToTripModal
